import { Filter as FilterIcon, Loader2 } from "lucide-react";
import { FOCUS_RING, cx } from "./tokens";

export interface FilterActionBarProps {
  onClear: () => void;
  onApply: () => void;
  applying?: boolean;
  /** Values chosen in the draft — drives whether clearing does anything. */
  selectedCount?: number;
  /** Draft values that differ from what is currently applied (§23.11). */
  pendingChanges?: number;
  /**
   * Live result preview (§23.10). No count endpoint exists yet, so this is
   * normally undefined and the button reads plainly; wiring a count later needs
   * no change here.
   */
  resultCount?: number;
  className?: string;
}

const numberFormat = new Intl.NumberFormat("pt-BR");

/**
 * Secondary action left, primary right, on a bordered surface above the
 * content, with mobile safe-area padding (§23.12).
 *
 * The primary label carries the consequence when it is knowable, rather than a
 * second line restating the first (§56).
 */
export default function FilterActionBar({
  onClear,
  onApply,
  applying = false,
  selectedCount = 0,
  pendingChanges = 0,
  resultCount,
  className,
}: FilterActionBarProps) {
  const noResults = resultCount === 0;

  const applyLabel =
    resultCount === undefined
      ? "Aplicar filtros"
      : `Aplicar filtros · ${numberFormat.format(resultCount)} ${
          resultCount === 1 ? "questão" : "questões"
        }`;

  return (
    <div
      className={cx(
        "sticky bottom-0 z-10 border-t border-edge bg-surface",
        "px-3 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-5",
        className,
      )}
    >
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-3">
        <button
          type="button"
          onClick={onClear}
          disabled={selectedCount === 0 || applying}
          className={cx(
            "h-10 w-full rounded-input border border-edge bg-surface px-4 text-sm font-medium text-ink-secondary sm:w-auto",
            "transition-colors hover:bg-surface-hover hover:text-ink",
            "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-surface",
            FOCUS_RING,
          )}
        >
          Limpar filtros
        </button>

        <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:gap-3">
          {pendingChanges > 0 && !applying ? (
            <span className="text-center text-xs text-ink-muted sm:text-left" aria-live="polite">
              {pendingChanges === 1
                ? "1 alteração não aplicada"
                : `${pendingChanges} alterações não aplicadas`}
            </span>
          ) : null}

          <button
            type="button"
            onClick={onApply}
            disabled={applying || noResults}
            className={cx(
              "inline-flex h-10 w-full items-center justify-center gap-2 rounded-input bg-brand px-5 sm:w-auto",
              "text-sm font-semibold text-brand-contrast transition-[filter,opacity]",
              "hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50",
              FOCUS_RING,
            )}
          >
            {applying ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <FilterIcon className="h-4 w-4" aria-hidden="true" />
            )}
            {applying ? "Aplicando…" : applyLabel}
          </button>
        </div>
      </div>

      {noResults ? (
        <p className="mt-2 text-center text-xs text-ink-muted sm:text-right">
          Nenhuma questão com esses filtros. Remova alguns para ampliar a busca.
        </p>
      ) : null}
    </div>
  );
}
